import { useEffect } from "react";

const ATTRIBUTE_NAME_LIST = "data-anv-dialogs";
const ATTRIBUTE_NAME_BOOL = "data-anv-dialogs-open";

/**
 * Props for the useDialogScrollLock hook
 * @property {boolean} open - Whether the dialog is open
 * @property {string} id - Unique identifier for the dialog
 * @property {boolean} [enableScrollChaining] - Whether to enable scroll chaining
 * @property {React.RefObject<HTMLElement>} elementRef - Reference to the dialog element
 */
interface UseDialogScrollLockProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;
  /**
   * Unique identifier for the dialog
   */
  id: string;
  /**
   * Whether to enable scroll chaining
   */
  enableScrollChaining?: boolean;
  /**
   * Reference to the dialog element
   */
  elementRef: React.RefObject<HTMLElement>;
}

/**
 * Custom hook for managing dialog scroll lock in shadow DOM environments.
 *
 * Features:
 * - Manages scroll lock state for dialogs in shadow DOM
 * - Tracks multiple open dialogs with unique IDs
 * - Prevents scroll chaining when dialogs are open
 * - Automatically cleans up scroll lock on unmount
 * - Supports enabling/disabling scroll chaining
 * - Uses data attributes to track dialog state
 *
 * @param props - Configuration object containing dialog state and element reference
 * @returns void
 */
export const useDialogScrollLock = ({
  open,
  id,
  enableScrollChaining,
  elementRef,
}: UseDialogScrollLockProps) => {
  useEffect(() => {
    if (enableScrollChaining) return () => {};
    const root = elementRef.current?.getRootNode();
    if (!(root instanceof ShadowRoot)) return () => {};

    const updateDialogAttributes = (shouldAdd: boolean) => {
      const currentDialogs =
        root.host
          .getAttribute(ATTRIBUTE_NAME_LIST)
          ?.split(" ")
          .filter(Boolean) ?? [];

      const newDialogs = new Set(currentDialogs);
      if (shouldAdd) {
        newDialogs.add(id);
      } else {
        newDialogs.delete(id);
      }

      const dialogList = Array.from(newDialogs);
      root.host.setAttribute(ATTRIBUTE_NAME_LIST, dialogList.join(" "));

      if (dialogList.length > 0) {
        root.host.setAttribute(ATTRIBUTE_NAME_BOOL, "true");
      } else {
        root.host.removeAttribute(ATTRIBUTE_NAME_BOOL);
      }
    };

    updateDialogAttributes(open);

    return () => {
      updateDialogAttributes(false);
    };
  }, [open, id, enableScrollChaining, elementRef]);
};
